<template>
  <div class="d-flex justify-end">
    <SoButton
      title="Export"
      icon="download"
      type="primary"
      large
      data-testid="elec-nominations-export-btn"
      @click="exportElecNominations()"
    />
  </div>
  <SoContainer fluid>
    <div>
      <p class="mb-4">
        Please select an Excel file with the Electricity Nominations you want to
        import
      </p>
    </div>
    <SoFileInputField
      v-model="file"
      class="mb-4"
      file-types=".xlsx, .xls"
      label="Upload File"
      placeholder="Select a file"
    />
    <div class="d-flex mb-4 gap-2">
      <SoButton
        title="Preview"
        data-testid="preview-elec-nomination-file-button"
        :disabled="file.length == 0"
        @click="previewContent(file[0])"
      />
      <SoButton
        title="Upload"
        type="primary"
        data-testid="upload-elec-nomination-file-button"
        :disabled="parsedData.length == 0"
        @click="uploadData()"
      />
    </div>
    <v-slide-x-transition>
      <SoAlert
        v-if="elecNominationsMutator.isError || parseError"
        type="error"
        class="my-4"
      >
        {{ elecNominationsMutator.error || parseError }}
      </SoAlert>
      <SoAlert
        v-else-if="elecNominationsMutator.isSuccess"
        type="success"
        class="my-4"
      >
        Electricity Nominations uploaded.
      </SoAlert>
    </v-slide-x-transition>
    <div v-if="parsedData.length > 0">
      <table>
        <thead>
          <tr>
            <th v-for="header in headers" :key="header">{{ header }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(row, index) in parsedData" :key="index">
            <td v-for="header in headers" :key="header">{{ row[header] }}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </SoContainer>
</template>
<script lang="ts">
  import SoContainer from "@/components/SoContainer.vue"
  import { defineComponent } from "vue"
  import SoButton from "@/components/formControls/SoButton.vue"
  import SoFileInputField from "@/components/formControls/SoFileInputField.vue"
  import * as xlsx from "xlsx-republish"
  import { TradesMutator } from "@/mutators/trades/TradesMutator"
  import SoAlert from "@/components/SoAlert.vue"
  import {
    HalfHourlyValue,
    IElecNominationsData,
  } from "@/models/operations/tradingData/elecNominationsData.types"
  import { TradeElecNominations } from "@/models/operations/tradingData/elecNominationsData"
  import dayjs, { Dayjs } from "dayjs"
  import {
    ElecNominationEntry,
    RawElecNominationData,
  } from "@/constants/tradeOptions"
  import { AuthService } from "@/services/AuthService"
  import { processElecNominationsData } from "@/helpers/tradeHelper"
  import { TradingDataService } from "@/services/operations/TradingDataService"

  export default defineComponent({
    components: {
      SoAlert,
      SoFileInputField,
      SoButton,
      SoContainer,
    },
    data() {
      return {
        elecNominationsMutator: TradesMutator.insertElecNominations(),
        file: [] as File[],
        parsedData: [] as ElecNominationEntry[],
        headers: [] as string[],
        loading: false,
        parseError: null as string | null,
      }
    },
    computed: {},
    watch: {
      file() {
        this.elecNominationsMutator.reset()
        this.parseError = null
        this.parsedData = []
      },
    },
    methods: {
      previewContent(file: File) {
        const reader = new FileReader()
        if (file.size > 1024 * 1024) {
          this.parseError = "File size should not exceed 1MB"
          return
        }
        reader.onload = (e) => {
          if (e.target) {
            const data = new Uint8Array(e.target.result as ArrayBuffer)
            const workbook = xlsx.read(data, { type: "array" })
            const firstSheet = workbook.Sheets[workbook.SheetNames[0]]
            const jsonData = xlsx.utils.sheet_to_json(firstSheet, {
              raw: false,
            }) as RawElecNominationData[]

            this.validateHeaders(jsonData)
            if (this.parseError) return

            this.parsedData = jsonData.map((row) => {
              return {
                Date: new Date(row.Date),
                Name: row.Name,
                Volume: Number(row.Volume),
                SP: Number(row.SP),
              }
            })

            this.headers = Object.keys(jsonData[0] || {})
          }
        }
        reader.onerror = () => {
          this.parseError = "Error reading file"
        }
        reader.readAsArrayBuffer(file)
      },
      uploadData() {
        if (this.parsedData.length == 0) {
          this.parseError = "Please select a file to upload"
        } else {
          const entities = this.processElecNominationsCSV(this.parsedData)
          entities.forEach((entity) => {
            this.elecNominationsMutator.mutateAsync({ ...entity })
          })
          this.parsedData = []
        }
      },
      processElecNominationsCSV(csvData: Array<ElecNominationEntry>) {
        const nominationsByDay = processElecNominationsData(csvData)
        let dateKey: keyof typeof nominationsByDay
        const elecNominationEntities: Array<IElecNominationsData> = []

        for (dateKey in nominationsByDay) {
          const dayNominations = nominationsByDay[
            dateKey
          ] as ElecNominationEntry[]
          if (![46, 48, 50].includes(dayNominations.length)) {
            this.parseError = `Invalid number of slots for day ${dateKey};
            expecting 46, 48 or 50 half-hourly volumes`
            break
          }

          const elecNomination = new TradeElecNominations(
            dayjs(dateKey),
            dayjs(),
            dayjs(),
            dayNominations.map((entry: ElecNominationEntry) => {
              return { timeSlot: entry.SP, volume: entry.Volume }
            }) as Array<HalfHourlyValue>,
            AuthService.getCurrentUser().name,
          )

          elecNominationEntities.push(elecNomination)
        }
        return elecNominationEntities
      },
      validateHeaders(jsonData: RawElecNominationData[]) {
        const headers = Object.keys(jsonData[0])
        if (
          headers.length !== 4 ||
          headers.filter((key) =>
            ["Name", "Date", "SP", "Volume"].includes(key),
          ).length !== 4
        ) {
          this.parseError = "Invalid headers"
        }
      },
      async exportElecNominations() {
        const elecNominations =
          await TradingDataService.getElecNominationsData().suspense?.()

        if (elecNominations) {
          const worksheet = xlsx.utils.json_to_sheet(
            elecNominations.data.map((item) => {
              return {
                ...item,
                date: (item.date as Dayjs).toDate(),
                createdAt: (item.createdAt as Dayjs).toDate(),
                updatedAt: (item.updatedAt as Dayjs).toDate(),
              }
            }),
            {
              dateNF: "yyyy-mm-dd hh:mm:ss",
            },
          )

          const workbook = xlsx.utils.book_new()
          xlsx.utils.book_append_sheet(workbook, worksheet, "Sheet1")
          xlsx.writeFile(
            workbook,
            `electricity_nominations_${dayjs().format("DDMMYY")}.xlsx`,
          )
        }
      },
    },
  })
</script>
<style scoped>
  table {
    border-collapse: collapse;
    width: 100%;
  }

  th,
  td {
    border: 1px solid #ddd;
    padding: 8px;
    text-align: left;
  }
</style>
